import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EditIcon from '@material-ui/icons/Edit';
import CancelIcon from '@material-ui/icons/Cancel';
import { firestore } from '../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  section: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
  },
  chip: {
    marginLeft: theme.spacing(1),
  },
  table: {
    marginTop: theme.spacing(2),
  },
  label: {
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
  },
}));

const BookingDetailPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBooking();
  }, [id]);

  const fetchBooking = async () => {
    setLoading(true);
    try {
      const bookingDoc = await getDoc(doc(firestore, 'bookings', id));
      
      if (bookingDoc.exists()) {
        setBooking({
          id: bookingDoc.id,
          ...bookingDoc.data()
        });
      } else {
        // Données de démonstration si le document n'existe pas
        const demoBooking = {
          id: id,
          tripId: '1',
          tripName: 'Paris - Lyon',
          departureDate: '2025-04-15T08:00:00',
          arrivalDate: '2025-04-15T12:30:00',
          passengerName: 'Alice Martin',
          passengerEmail: 'alice.martin@example.com',
          passengerPhone: '+33612345678',
          seats: 2,
          totalPrice: 91.98,
          status: 'Confirmed',
          bookingDate: '2025-03-20T14:30:00',
          paymentMethod: 'Carte bancaire',
          paymentStatus: 'Paid',
          notes: 'Réservation pour un voyage d\'affaires'
        };
        setBooking(demoBooking);
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/bookings');
  };

  const handleEdit = () => {
    navigate(`/admin/bookings/${id}/edit`);
  };

  const handleCancel = () => {
    navigate(`/admin/bookings/${id}/cancel`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed':
        return 'primary';
      case 'Pending':
        return 'default';
      case 'Cancelled':
        return 'secondary';
      case 'Completed':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'primary';
      case 'Pending':
        return 'default';
      case 'Failed':
        return 'secondary';
      case 'Refunded':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  };

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress />
      </Box>
    );
  }

  if (!booking) {
    return (
      <Box className={classes.root}>
        <Paper className={classes.paper}>
          <Typography variant="h6">Réservation non trouvée</Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            style={{ marginTop: '16px' }}
          >
            Retour à la liste
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Paper className={classes.paper}>
        <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={2}>
          <Box display="flex" alignItems="center">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              style={{ marginRight: '16px' }}
            >
              Retour
            </Button>
            <Typography variant="h4" component="h1">
              Réservation #{booking.id}
            </Typography>
            <Chip 
              label={booking.status} 
              color={getStatusColor(booking.status)}
              className={classes.chip}
            />
          </Box>
          <div className={classes.buttons}>
            {booking.status !== 'Cancelled' && (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                >
                  Modifier
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                >
                  Annuler
                </Button>
              </>
            )}
          </div>
        </Box>

        <Divider />

        <div className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Informations du voyage
          </Typography>
          <Table className={classes.table}>
            <TableBody>
              <TableRow>
                <TableCell className={classes.label}>Voyage</TableCell>
                <TableCell>{booking.tripName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.label}>Date de départ</TableCell>
                <TableCell>{formatDate(booking.departureDate)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.label}>Date d'arrivée</TableCell>
                <TableCell>{formatDate(booking.arrivalDate)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.label}>Nombre de places</TableCell>
                <TableCell>{booking.seats}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <Divider />

        <div className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Informations du passager
          </Typography>
          <Table className={classes.table}>
            <TableBody>
              <TableRow>
                <TableCell className={classes.label}>Nom</TableCell>
                <TableCell>{booking.passengerName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.label}>Email</TableCell>
                <TableCell>{booking.passengerEmail}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.label}>Téléphone</TableCell>
                <TableCell>{booking.passengerPhone}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <Divider />

        <div className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Informations de paiement
          </Typography>
          <Table className={classes.table}>
            <TableBody>
              <TableRow>
                <TableCell className={classes.label}>Prix total</TableCell>
                <TableCell>{formatPrice(booking.totalPrice)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.label}>Méthode de paiement</TableCell>
                <TableCell>{booking.paymentMethod}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.label}>Statut du paiement</TableCell>
                <TableCell>
                  <Chip 
                    label={booking.paymentStatus} 
                    color={getPaymentStatusColor(booking.paymentStatus)}
                    size="small"
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.label}>Date de réservation</TableCell>
                <TableCell>{formatDate(booking.bookingDate)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {booking.notes && (
          <>
            <Divider />
            <div className={classes.section}>
              <Typography variant="h6" className={classes.sectionTitle}>
                Notes
              </Typography>
              <Typography variant="body1">
                {booking.notes}
              </Typography>
            </div>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default BookingDetailPage;
